import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useFetch from "../../../hooks/useFetch";
import { ITicketAutomationReceivedRule } from "../../../services/tasySchedMenu.service";
import { ScheduleAppointmentService } from "../../Schedule/appointment.service";
import { TAppointment } from "../../Schedule/NewSchedule/schema";
import {
  ICalendarConsultationAvailableBot,
  ICalendarConsultationAvailableBotSchedule,
} from "./models";
import AvailabilityNotFound from "./AvailabilityNotFound";
import AvailabilitySearchLoading from "./AvailabilitySearchLoading";

interface IConsultationBotScheduleProps {
  rule?: ITicketAutomationReceivedRule | null;
  menuName?: string;
}

interface IPage {
  start?: string;
  items: ICalendarConsultationAvailableBot[];
}

const getInsuranceIntegrationId = (insurance: any) =>
  insurance?.insuranceIntegrationId || insurance?.code || insurance?.id;

const formatButtonDate = (value?: string) =>
  value ? dayjs(value).format("DD/MM/YYYY [as] HH:mm") : "";

const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const STATUS_POLL_INTERVAL_MS = 3000;
const FALLBACK_POLL_INTERVAL_MS = 10000;

const FEATURED_CONSULTATION_AUTOMATION_CODE = "26";

const ConsultationBotSchedule: React.FC<IConsultationBotScheduleProps> = ({
  rule,
  menuName,
}) => {
  const [pages, setPages] = useState<IPage[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const { t } = useTranslation();
  const activeRequest = useRef(0);
  const { control, getValues, setValue, resetField } =
    useFormContext<TAppointment>();
  const selectedSchedule = useWatch({ control, name: "schedule" }) as any;
  const isFeaturedConsultation =
    rule?.ticketAutomationCode === FEATURED_CONSULTATION_AUTOMATION_CODE;

  const { sendRequest: createBot } = useFetch(
    ScheduleAppointmentService.createConsultationAvailableBotTasy2,
  );
  const { sendRequest: getBot } = useFetch(
    ScheduleAppointmentService.getConsultationAvailableBotTasy2,
  );

  const currentPage = pages[pageIndex];
  const groupedSchedules = useMemo(
    () => currentPage?.items || [],
    [currentPage],
  );
  const schedules = useMemo(
    () => groupedSchedules.flatMap((group) => group.schedules || []),
    [groupedSchedules],
  );

  const createSearch = useCallback(
    async (start?: string) => {
      if (!rule?.id) return null;

      const { patient, insurance, search, establishment } = getValues() as any;
      const response = await createBot({
        individualId: patient.individualId || patient.id,
        ticketAutomationReceivedRuleId: rule.id,
        insuranceIdentifier: Number(getInsuranceIntegrationId(insurance)),
        insuranceCategoryCode: insurance?.categoryIntegrationId,
        establishmentCode: establishment?.integrationId,
        medicalSpecialtyCode: search.speciality?.id
          ? Number(search.speciality.id)
          : undefined,
        calendarIdentifier: search.professional?.id
          ? Number(search.professional.id)
          : undefined,
        calendarPreference: search.calendarPreference || undefined,
        calendarDateStart: start
          ? dayjs(start).toISOString()
          : dayjs(search.date).toISOString(),
        scheduleShiftCodeList: search.period?.length
          ? search.period.map((p: any) => p.code)
          : undefined,
      });

      return response.success && response.data?.identifier
        ? response.data.identifier
        : null;
    },
    [createBot, getValues, rule?.id],
  );

  const getStatus = useCallback(async (calendarControlId: number) => {
    try {
      const response = await ScheduleAppointmentService.getConsultationAvailableBotStatusTasy2(
        { CalendarControlId: calendarControlId },
      );
      return response.data;
    } catch {
      return null;
    }
  }, []);

  const poll = useCallback(
    async (calendarControlId: number, requestId: number) => {
      const deadline = Date.now() + POLL_TIMEOUT_MS;

      while (Date.now() < deadline) {
        if (activeRequest.current !== requestId) return null;

        const status = await getStatus(calendarControlId);

        if (status?.isCompleted) {
          // Lista finalizada sem nenhum horário válido: não precisa buscar os dados
          if (status.validItems === 0) return [];

          const { data, success } = await getBot({
            CalendarControlId: calendarControlId,
          });

          return success && data ? data : [];
        }

        if (!status) {
          // Status indisponível: mantém o comportamento anterior (consulta os dados)
          const { data, success } = await getBot({
            CalendarControlId: calendarControlId,
          });

          if (success && data?.length) {
            return data;
          }
        }

        await new Promise((resolve) =>
          setTimeout(
            resolve,
            status ? STATUS_POLL_INTERVAL_MS : FALLBACK_POLL_INTERVAL_MS,
          ),
        );
      }

      return [];
    },
    [getBot, getStatus],
  );

  const fetchPage = useCallback(
    async (start?: string, append = false) => {
      const requestId = activeRequest.current + 1;
      activeRequest.current = requestId;
      setLoading(true);
      setNotFound(false);
      resetField("schedule");

      const createdControlId = await createSearch(start);
      if (!createdControlId) {
        setLoading(false);
        setNotFound(true);
        return;
      }

      const data = await poll(createdControlId, requestId);
      if (activeRequest.current !== requestId) return;

      const nextPage = { start, items: data || [] };
      setPages((previous) => (append ? [...previous, nextPage] : [nextPage]));
      setPageIndex((previous) => (append ? previous + 1 : 0));
      setNotFound(!data?.length);
      setLoading(false);
    },
    [createSearch, poll, resetField],
  );

  const onSelect = useCallback(
    (schedule: ICalendarConsultationAvailableBotSchedule) => {
      if (selectedSchedule?.identifier === schedule.identifier) {
        resetField("schedule");
        return;
      }

      setValue("schedule", {
        descriptionSite: schedule.descriptionSite?.trim() || schedule.calendarName,
        identifier: schedule.identifier,
        calendarStartDate: schedule.calendarStartDate,
        calendarEndDate: schedule.calendarEndDate,
        minutesDuration: schedule.minutesDuration,
        calendarIdentifier: schedule.calendarIdentifier,
        insuranceCode: schedule.insuranceCode,
        calendarAddress: schedule.calendarAddress,
        orientation: schedule.orientation,
      });
    },
    [resetField, selectedSchedule?.identifier, setValue],
  );

  const nextPage = useCallback(() => {
    const last = schedules[schedules.length - 1];
    if (!last?.calendarStartDate) return;

    fetchPage(
      dayjs(last.calendarStartDate).add(1, "minute").toISOString(),
      true,
    );
  }, [fetchPage, schedules]);

  useEffect(() => {
    fetchPage();

    return () => {
      activeRequest.current += 1;
    };
  }, [fetchPage]);

  return (
    <Stack gap={3}>
      {loading && <AvailabilitySearchLoading />}

      {!loading && notFound && <AvailabilityNotFound />}

      {!loading && !!groupedSchedules.length && (
        <>
          {pageIndex > 0 && (
            <Box>
              <Button
                variant="outlined"
                onClick={() => setPageIndex(pageIndex - 1)}
              >
                {t("page.schedules.newSchedule.schedule.bot.before")}{" "}
                {formatButtonDate(schedules[0]?.calendarStartDate)}
              </Button>
            </Box>
          )}

          <Grid container spacing={isFeaturedConsultation ? 2.5 : 3}>
            {groupedSchedules.map((group, index) => {
              const date = dayjs(group.calendarStartDate || group.calendarDate);
              const professional =
                group.physicianNameExecute || group.physicianNickName;
              const title =
                isFeaturedConsultation && menuName
                  ? menuName
                  : group.groupingName ||
                    group.descriptionSite?.trim() ||
                    group.calendarName;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={`${group.calendarIdentifier}-${date.format("YYYY-MM-DD")}`}
                >
                  <Stack gap={isFeaturedConsultation ? 1 : 1.5}>
                    {index >= 3 && !isFeaturedConsultation && (
                      <Divider sx={{ mb: 1 }} />
                    )}
                    <Stack direction="row" gap={1} alignItems="center">
                      <CalendarMonthIcon fontSize="small" color="primary" />
                      <Box>
                        <Typography
                          variant={isFeaturedConsultation ? "h6" : "h5"}
                          fontWeight={700}
                          color="primary.main"
                        >
                          {date.format("DD [de] MMMM [de] YYYY")}
                        </Typography>
                        <Typography
                          variant={isFeaturedConsultation ? "subtitle2" : "h6"}
                          fontWeight={700}
                          color="primary.main"
                        >
                          {date.format("dddd")}
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography
                      variant={isFeaturedConsultation ? "subtitle2" : "body1"}
                      color="primary.main"
                      fontWeight={isFeaturedConsultation ? 700 : 400}
                      sx={{
                        textTransform: isFeaturedConsultation
                          ? "uppercase"
                          : "none",
                      }}
                    >
                      {title}
                    </Typography>

                    {!isFeaturedConsultation && professional && (
                      <Typography variant="body2" color="primary.main">
                        <strong>
                          {t(
                            "page.schedules.newSchedule.schedule.bot.professional",
                          )}
                          :
                        </strong>
                        <br />
                        <strong>{professional}</strong>
                      </Typography>
                    )}

                    <Stack direction="row" gap={0.75} flexWrap="wrap">
                      {group.schedules.map((schedule) => {
                        const selected =
                          selectedSchedule?.identifier === schedule.identifier;

                        return (
                          <Button
                            key={schedule.identifier}
                            variant={selected ? "contained" : "outlined"}
                            color="primary"
                            onClick={() => onSelect(schedule)}
                            sx={{
                              minWidth: isFeaturedConsultation ? 70 : 68,
                              fontWeight: 700,
                              px: isFeaturedConsultation ? 1.75 : undefined,
                            }}
                          >
                            {dayjs(schedule.calendarStartDate).format("HH:mm")}
                          </Button>
                        );
                      })}
                    </Stack>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>

          {schedules.length >= 10 && (
            <Box>
              <Button variant="outlined" onClick={nextPage}>
                {t("page.schedules.newSchedule.schedule.bot.after")}{" "}
                {formatButtonDate(
                  schedules[schedules.length - 1]?.calendarStartDate,
                )}
              </Button>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

export default memo(ConsultationBotSchedule);
